/**
 * CallOverlay — In-window call overlay for Huddle01 voice/video calls.
 *
 * Architecture:
 * - CallOverlay: thin wrapper, reads activeCall from store, renders in messenger
 * - MockCallOverlayContent: DEV mode — zero Huddle01 hooks, purely store-driven
 * - HuddleCallOverlayContent: production — Huddle01 hooks for real WebRTC
 * - CallOverlayErrorBoundary: catches any hook crash, shows fallback
 *
 * DEV_AUTH_BYPASS (mock rooms):
 * - NO Huddle01 hooks are imported or called
 * - Mock rooms detected via `isMockRoom()` — roomId starts with "mock-room-"
 * - The entire UI runs on Zustand store state
 *
 * Polish features:
 * - Error recovery with retry button + auto-dismiss
 * - In-window by default, fullscreen only via browser Fullscreen API
 * - Entry/exit CSS transitions
 * - Keyboard shortcuts (M = mute, V = camera, F = fullscreen, Esc = end)
 * - Participant count in header
 * - Scoped to messenger so calls do not take over the whole shell by default
 */

import {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  Component,
  type ReactNode,
  type ErrorInfo,
  type MouseEvent,
} from "react";
import {
  Mic,
  MicOff,
  Camera,
  CameraOff,
  MonitorUp,
  MonitorOff,
  PhoneOff,
  Users,
  Maximize2,
  Minimize2,
  Video,
  Phone,
  AlertCircle,
  RotateCcw,
  X,
  ChevronDown,
  Settings,
  Volume2,
  VolumeX,
  Zap,
} from "lucide-react";
import {
  useDataMessage,
  useDevices,
  useLocalAudio,
  useLocalPeer,
  useLocalScreenShare,
  useLocalVideo,
  usePeerIds,
  useRemoteAudio,
  useRemotePeer,
  useRemoteVideo,
  useRoom,
} from "@huddle01/react";
import { cn } from "@slumbr/ui";

import {
  getCurrentUserId,
  getCurrentUserName,
  useMessengerStore,
} from "../stores/messenger-store";
import { getDisplayName } from "./messenger-layout";
import { isMockRoom } from "../huddle01/config";
import type { CallSession, LocalCallState } from "../types";

const CALL_DATA_LABEL = "slumbr.call.control";

type CallPeerMetadata = {
  userId?: string;
  displayName?: string;
  avatarUrl?: string;
};

type PeerAudioControl = {
  volume: number;
  deafened: boolean;
};

type PeerContextMenuState = {
  peerId: string;
  displayName: string;
  x: number;
  y: number;
};

type SinkSelectableMediaElement = HTMLMediaElement & {
  setSinkId?: (sinkId: string) => Promise<void>;
};

function getDeviceLabel(device: MediaDeviceInfo, fallback: string) {
  return device.label || fallback;
}

// ─── Error Boundary ─────────────────────────────────────────────────────────

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class CallOverlayErrorBoundary extends Component<
  { children: ReactNode; onEndCall: () => void },
  ErrorBoundaryState
> {
  constructor(props: { children: ReactNode; onEndCall: () => void }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[CallOverlay] Caught error:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-gray-950">
          <AlertCircle className="h-12 w-12 text-red-400" />
          <h2 className="text-lg font-semibold text-white">
            Call Error
          </h2>
          <p className="max-w-md text-center text-sm text-white/50">
            Something went wrong with the call overlay.
            {this.state.error?.message && (
              <span className="mt-1 block text-xs text-red-400/70">
                {this.state.error.message}
              </span>
            )}
          </p>
          <button
            onClick={() => {
              this.setState({ hasError: false, error: null });
              this.props.onEndCall();
            }}
            className="rounded-lg bg-red-500 px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600"
          >
            End Call
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// ─── Call Timer ──────────────────────────────────────────────────────────────

function CallTimer({ startedAt }: { startedAt: string }) {
  const [elapsed, setElapsed] = useState("0:00");

  useEffect(() => {
    const start = new Date(startedAt).getTime();
    const tick = () => {
      const diff = Date.now() - start;
      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setElapsed(`${minutes}:${seconds.toString().padStart(2, "0")}`);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [startedAt]);

  return (
    <span className="font-mono text-sm text-white/70">{elapsed}</span>
  );
}

// ─── Local Video Preview (avatar fallback) ───────────────────────────────────

function LocalAvatarPreview({ displayName }: { displayName: string }) {
  return (
    <div className="flex h-full w-full items-center justify-center rounded-2xl bg-surface-dark-2/80">
      <div className="flex flex-col items-center gap-2">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-brand-500/20 text-2xl font-bold text-brand-400">
          {displayName.slice(0, 2).toUpperCase()}
        </div>
        <span className="text-sm text-white/60">{displayName}</span>
      </div>
    </div>
  );
}

function StreamVideo({
  stream,
  muted = false,
  className,
}: {
  stream: MediaStream | null;
  muted?: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (ref.current) ref.current.srcObject = stream;
  }, [stream]);

  if (!stream) return null;

  return (
    <video
      ref={ref}
      autoPlay
      playsInline
      muted={muted}
      className={className}
    />
  );
}

function StreamAudio({
  stream,
  volume = 1,
  sinkId,
  muted = false,
}: {
  stream: MediaStream | null;
  volume?: number;
  sinkId?: string;
  muted?: boolean;
}) {
  const ref = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    if (ref.current) ref.current.srcObject = stream;
  }, [stream]);

  useEffect(() => {
    if (!ref.current) return;
    ref.current.volume = Math.max(0, Math.min(1, volume));
    ref.current.muted = muted;
  }, [muted, volume]);

  useEffect(() => {
    if (!ref.current || !sinkId) return;
    const audio = ref.current as SinkSelectableMediaElement;
    audio.setSinkId?.(sinkId).catch(() => {});
  }, [sinkId]);

  if (!stream) return null;
  return <audio ref={ref} autoPlay />;
}

function RemotePeerTile({
  peerId,
  audioControl,
  speakerDeviceId,
  onContextMenu,
}: {
  peerId: string;
  audioControl: PeerAudioControl;
  speakerDeviceId?: string;
  onContextMenu: (
    event: MouseEvent<HTMLDivElement>,
    peerId: string,
    displayName: string
  ) => void;
}) {
  const { stream: videoStream, isVideoOn } = useRemoteVideo({ peerId });
  const { stream: audioStream, isAudioOn } = useRemoteAudio({ peerId });
  const { metadata } = useRemotePeer<CallPeerMetadata>({ peerId });
  const displayName = metadata?.displayName ?? "Guest";

  return (
    <div
      className="relative min-h-0 overflow-hidden rounded-2xl bg-surface-dark-2 transition-transform hover:scale-[1.01]"
      onContextMenu={(event) => onContextMenu(event, peerId, displayName)}
    >
      {isVideoOn && videoStream ? (
        <StreamVideo stream={videoStream} className="h-full w-full object-cover" />
      ) : (
        <LocalAvatarPreview displayName={displayName} />
      )}
      {isAudioOn && !audioControl.deafened && (
        <StreamAudio
          stream={audioStream}
          volume={audioControl.volume}
          sinkId={speakerDeviceId}
        />
      )}
      <div className="absolute left-2 top-2 rounded-md bg-black/40 px-2 py-1 text-[10px] text-white/80">
        {displayName}
      </div>
      {audioControl.deafened && (
        <div className="absolute right-2 top-2 rounded-md bg-red-500/30 px-2 py-1 text-[10px] text-red-100">
          Deafened
        </div>
      )}
      <div className="absolute bottom-2 right-2 rounded-md bg-black/40 px-2 py-1 text-[10px] text-white/60">
        Right-click for controls
      </div>
    </div>
  );
}

function DeviceSelect({
  label,
  value,
  devices,
  fallbackLabel,
  onChange,
}: {
  label: string;
  value: string;
  devices: MediaDeviceInfo[];
  fallbackLabel: string;
  onChange: (deviceId: string) => void;
}) {
  return (
    <label className="block space-y-1.5">
      <span className="text-[11px] font-medium uppercase tracking-wide text-white/40">
        {label}
      </span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-lg border border-white/10 bg-surface-dark-2 px-3 py-2 text-sm text-white outline-none transition-colors focus:border-brand-500/60"
      >
        <option value="">System default</option>
        {devices.map((device, index) => (
          <option key={device.deviceId || `${label}-${index}`} value={device.deviceId}>
            {getDeviceLabel(device, `${fallbackLabel} ${index + 1}`)}
          </option>
        ))}
      </select>
    </label>
  );
}

function CallSettingsPanel({
  micDevices,
  cameraDevices,
  speakerDevices,
  selectedMicId,
  selectedCameraId,
  selectedSpeakerId,
  onSelectMic,
  onSelectCamera,
  onSelectSpeaker,
  onRefreshDevices,
}: {
  micDevices: MediaDeviceInfo[];
  cameraDevices: MediaDeviceInfo[];
  speakerDevices: MediaDeviceInfo[];
  selectedMicId: string;
  selectedCameraId: string;
  selectedSpeakerId: string;
  onSelectMic: (deviceId: string) => void;
  onSelectCamera: (deviceId: string) => void;
  onSelectSpeaker: (deviceId: string) => void;
  onRefreshDevices: () => void;
}) {
  return (
    <div className="absolute bottom-24 right-4 z-20 w-[min(360px,calc(100vw-2rem))] rounded-2xl border border-white/10 bg-gray-950/95 p-4 shadow-2xl backdrop-blur-xl sm:right-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold text-white">Call Settings</h4>
          <p className="text-xs text-white/40">Choose your input and output devices.</p>
        </div>
        <button
          onClick={onRefreshDevices}
          className="rounded-lg bg-white/10 px-2.5 py-1.5 text-xs font-medium text-white/70 transition-colors hover:bg-white/15 hover:text-white"
        >
          Refresh
        </button>
      </div>
      <div className="space-y-3">
        <DeviceSelect
          label="Microphone"
          value={selectedMicId}
          devices={micDevices}
          fallbackLabel="Microphone"
          onChange={onSelectMic}
        />
        <DeviceSelect
          label="Camera"
          value={selectedCameraId}
          devices={cameraDevices}
          fallbackLabel="Camera"
          onChange={onSelectCamera}
        />
        <DeviceSelect
          label="Speaker"
          value={selectedSpeakerId}
          devices={speakerDevices}
          fallbackLabel="Speaker"
          onChange={onSelectSpeaker}
        />
        {speakerDevices.length === 0 && (
          <p className="text-[11px] text-white/35">
            Speaker selection is only available in browsers that expose audio output devices.
          </p>
        )}
      </div>
    </div>
  );
}

function PeerContextMenu({
  menu,
  control,
  onClose,
  onVolumeChange,
  onToggleDeafen,
  onPoke,
}: {
  menu: PeerContextMenuState;
  control: PeerAudioControl;
  onClose: () => void;
  onVolumeChange: (peerId: string, volume: number) => void;
  onToggleDeafen: (peerId: string) => void;
  onPoke: (peerId: string) => void;
}) {
  useEffect(() => {
    const handleClose = () => onClose();
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("mousedown", handleClose);
    window.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("mousedown", handleClose);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  return (
    <div
      className="fixed z-[120] w-64 rounded-2xl border border-white/10 bg-gray-950/95 p-3 shadow-2xl backdrop-blur-xl"
      style={{ left: menu.x, top: menu.y }}
      onMouseDown={(event) => event.stopPropagation()}
      onContextMenu={(event) => event.preventDefault()}
    >
      <div className="mb-3 border-b border-white/10 pb-2">
        <p className="truncate text-sm font-semibold text-white">{menu.displayName}</p>
        <p className="text-[11px] text-white/40">Participant controls</p>
      </div>

      <label className="block space-y-2 rounded-xl bg-white/[0.03] p-3">
        <div className="flex items-center justify-between text-xs text-white/60">
          <span>Per-user volume</span>
          <span>{Math.round(control.volume * 100)}%</span>
        </div>
        <input
          type="range"
          min={0}
          max={100}
          value={Math.round(control.volume * 100)}
          onChange={(event) =>
            onVolumeChange(menu.peerId, Number(event.target.value) / 100)
          }
          className="w-full accent-brand-500"
        />
      </label>

      <div className="mt-2 space-y-1">
        <button
          onClick={() => onToggleDeafen(menu.peerId)}
          className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-white/75 transition-colors hover:bg-white/10 hover:text-white"
        >
          {control.deafened ? (
            <Volume2 className="h-4 w-4" />
          ) : (
            <VolumeX className="h-4 w-4" />
          )}
          {control.deafened ? "Undeafen User" : "Deafen User"}
        </button>
        <button
          onClick={() => onPoke(menu.peerId)}
          className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-white/75 transition-colors hover:bg-white/10 hover:text-white"
        >
          <Zap className="h-4 w-4 text-yellow-300" />
          Poke User
        </button>
      </div>
    </div>
  );
}

// ─── Connecting / Ringing Screen ─────────────────────────────────────────────

function ConnectingScreen({
  callType,
  conversationName,
  status,
}: {
  callType: "voice" | "video";
  conversationName: string;
  status: string;
}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-6">
      {/* Pulsing avatar ring */}
      <div className="relative">
        <div className="absolute inset-0 animate-ping rounded-full bg-brand-500/20" />
        <div className="relative flex h-28 w-28 items-center justify-center rounded-full bg-surface-dark-2 ring-4 ring-brand-500/30">
          {callType === "video" ? (
            <Video className="h-10 w-10 text-brand-400" />
          ) : (
            <Phone className="h-10 w-10 text-brand-400" />
          )}
        </div>
      </div>

      <div className="text-center">
        <h2 className="text-xl font-semibold text-white">{conversationName}</h2>
        <p className="mt-1 text-sm text-white/50">
          {status === "creating"
            ? "Setting up call..."
            : status === "connecting"
              ? "Connecting..."
              : status === "ringing"
                ? "Ringing..."
                : status === "reconnecting"
                  ? "Reconnecting..."
                  : "Starting call..."}
        </p>
      </div>

      {/* Animated dots */}
      <div className="flex gap-1.5">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="h-2 w-2 rounded-full bg-brand-500"
            style={{
              animation: "callPulse 1.4s ease-in-out infinite",
              animationDelay: `${i * 0.2}s`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Mock Active Call View (no Huddle01 hooks) ───────────────────────────────

function MockActiveCallView({
  isCameraOn,
  isScreenSharing,
}: {
  isCameraOn: boolean;
  isScreenSharing: boolean;
}) {
  return (
    <div className="flex-1 grid grid-cols-1 gap-2 p-3 sm:grid-cols-2 sm:p-4">
      {/* Local participant */}
      <div className="relative min-h-0 overflow-hidden rounded-2xl bg-surface-dark-2">
        <LocalAvatarPreview displayName="You" />
        {isCameraOn && (
          <div className="absolute left-2 top-2 rounded-md bg-brand-500/30 px-1.5 py-0.5 text-[10px] text-brand-300">
            Camera On (mock)
          </div>
        )}
        {isScreenSharing && (
          <div className="absolute right-2 top-2 rounded-md bg-red-500/30 px-1.5 py-0.5 text-[10px] text-red-300">
            Sharing Screen (mock)
          </div>
        )}
      </div>

      {/* Empty slot — waiting for others */}
      <div className="flex items-center justify-center rounded-2xl border-2 border-dashed border-white/10 bg-surface-dark-2/30">
        <div className="text-center">
          <Users className="mx-auto h-8 w-8 text-white/20" />
          <p className="mt-2 text-sm text-white/30">
            Waiting for others to join...
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Call Controls Bar ───────────────────────────────────────────────────────

function CallControls({
  isMicOn,
  isCameraOn,
  isScreenSharing,
  isFullscreen,
  isSettingsOpen = false,
  callType,
  onToggleMic,
  onToggleCamera,
  onToggleScreenShare,
  onToggleFullscreen,
  onToggleSettings,
  onEndCall,
}: {
  isMicOn: boolean;
  isCameraOn: boolean;
  isScreenSharing: boolean;
  isFullscreen: boolean;
  isSettingsOpen?: boolean;
  callType: "voice" | "video";
  onToggleMic: () => void;
  onToggleCamera: () => void;
  onToggleScreenShare: () => void;
  onToggleFullscreen: () => void;
  onToggleSettings?: () => void;
  onEndCall: () => void;
}) {
  return (
    <div className="flex items-center justify-center gap-2 pb-8 pt-4 sm:gap-3">
      {/* Mic toggle */}
      <button
        onClick={onToggleMic}
        className={cn(
          "flex h-11 w-11 items-center justify-center rounded-full transition-all sm:h-12 sm:w-12",
          isMicOn
            ? "bg-surface-dark-2/80 text-white hover:bg-surface-dark-3"
            : "bg-red-500 text-white hover:bg-red-600"
        )}
        aria-label={isMicOn ? "Mute microphone (M)" : "Unmute microphone (M)"}
      >
        {isMicOn ? (
          <Mic className="h-5 w-5" />
        ) : (
          <MicOff className="h-5 w-5" />
        )}
      </button>

      {/* Camera toggle */}
      <button
        onClick={onToggleCamera}
        className={cn(
          "flex h-11 w-11 items-center justify-center rounded-full transition-all sm:h-12 sm:w-12",
          isCameraOn
            ? "bg-surface-dark-2/80 text-white hover:bg-surface-dark-3"
            : "bg-red-500 text-white hover:bg-red-600"
        )}
        aria-label={isCameraOn ? "Turn off camera (V)" : "Turn on camera (V)"}
      >
        {isCameraOn ? (
          <Camera className="h-5 w-5" />
        ) : (
          <CameraOff className="h-5 w-5" />
        )}
      </button>

      {/* Screen share toggle */}
      <button
        onClick={onToggleScreenShare}
        className={cn(
          "flex h-11 w-11 items-center justify-center rounded-full transition-all sm:h-12 sm:w-12",
          isScreenSharing
            ? "bg-brand-500 text-white hover:bg-brand-600"
            : "bg-surface-dark-2/80 text-white hover:bg-surface-dark-3"
        )}
        aria-label={
          isScreenSharing ? "Stop screen sharing" : "Share screen"
        }
      >
        {isScreenSharing ? (
          <MonitorOff className="h-5 w-5" />
        ) : (
          <MonitorUp className="h-5 w-5" />
        )}
      </button>

      {/* Fullscreen toggle */}
      <button
        onClick={onToggleFullscreen}
        className="flex h-11 w-11 items-center justify-center rounded-full bg-surface-dark-2/80 text-white transition-all hover:bg-surface-dark-3 sm:h-12 sm:w-12"
        aria-label={isFullscreen ? "Exit fullscreen (F)" : "Fullscreen (F)"}
      >
        {isFullscreen ? (
          <Minimize2 className="h-5 w-5" />
        ) : (
          <Maximize2 className="h-5 w-5" />
        )}
      </button>

      {onToggleSettings && (
        <button
          onClick={onToggleSettings}
          className={cn(
            "flex h-11 w-11 items-center justify-center rounded-full text-white transition-all sm:h-12 sm:w-12",
            isSettingsOpen
              ? "bg-brand-500 hover:bg-brand-600"
              : "bg-surface-dark-2/80 hover:bg-surface-dark-3"
          )}
          aria-label="Call settings"
        >
          <Settings className="h-5 w-5" />
        </button>
      )}

      {/* End call */}
      <button
        onClick={onEndCall}
        className="flex h-12 w-12 items-center justify-center rounded-full bg-red-500 text-white transition-all hover:bg-red-600 hover:scale-105 sm:h-14 sm:w-14"
        aria-label="End call (Esc)"
      >
        <PhoneOff className="h-5 w-5 sm:h-6 sm:w-6" />
      </button>
    </div>
  );
}

// ─── Mock Call Overlay Content (DEV mode — zero Huddle01 hooks) ──────────────
// This is the version used for mock rooms. No Huddle01 imports at all.
// All state comes from the Zustand store.

function MockCallOverlayContent({
  activeCall,
  localCallState,
  conversationName,
  onMinimize,
  isHidden,
}: {
  activeCall: CallSession;
  localCallState: LocalCallState;
  conversationName: string;
  onMinimize: () => void;
  isHidden: boolean;
}) {
  const endCall = useMessengerStore((s) => s.endCall);
  const toggleMic = useMessengerStore((s) => s.toggleMic);
  const toggleCamera = useMessengerStore((s) => s.toggleCamera);
  const toggleScreenShare = useMessengerStore((s) => s.toggleScreenShare);
  const setCallStatus = useMessengerStore((s) => s.setCallStatus);
  const callError = useMessengerStore((s) => s.callError);
  const setCallError = useMessengerStore((s) => s.setCallError);
  const startCall = useMessengerStore((s) => s.startCall);

  const overlayRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // ─── Entry animation ──────────────────────────────────────────────────
  useEffect(() => {
    requestAnimationFrame(() => setIsVisible(true));
  }, []);

  // ─── Auto-transition from "connecting" to "active" (mock delay) ───────
  useEffect(() => {
    if (activeCall.status === "connecting") {
      const timer = setTimeout(() => {
        setCallStatus("active");
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [activeCall.status, setCallStatus]);

  // ─── Auto-dismiss errors after 8s ─────────────────────────────────────
  useEffect(() => {
    if (!callError) return;
    const timer = setTimeout(() => setCallError(null), 8000);
    return () => clearTimeout(timer);
  }, [callError, setCallError]);

  // ─── Fullscreen ───────────────────────────────────────────────────────
  const handleToggleFullscreen = useCallback(() => {
    if (!overlayRef.current) return;

    if (!document.fullscreenElement) {
      overlayRef.current.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(() => {});
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // ─── Handle end call ──────────────────────────────────────────────────
  const handleEndCall = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
    endCall();
  }, [endCall]);

  // ─── Handle retry ─────────────────────────────────────────────────────
  const handleRetry = useCallback(() => {
    setCallError(null);
    const { conversationId, type } = activeCall;
    endCall();
    startCall(conversationId, type);
  }, [activeCall, setCallError, endCall, startCall]);

  // ─── Keyboard shortcuts ───────────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      switch (e.key.toLowerCase()) {
        case "m":
          toggleMic();
          break;
        case "v":
          toggleCamera();
          break;
        case "f":
          handleToggleFullscreen();
          break;
        case "escape":
          // Exit browser fullscreen first; otherwise minimize
          if (document.fullscreenElement) {
            document.exitFullscreen().catch(() => {});
          } else {
            onMinimize();
          }
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [toggleMic, toggleCamera, handleToggleFullscreen, onMinimize]);

  // ─── Render ───────────────────────────────────────────────────────────
  const isConnecting =
    activeCall.status === "creating" ||
    activeCall.status === "connecting" ||
    activeCall.status === "ringing" ||
    activeCall.status === "reconnecting";

  return (
    <div
      ref={overlayRef}
      className={cn(
        "absolute inset-0 z-50 flex flex-col bg-gray-950 transition-all duration-300",
        isHidden
          ? "pointer-events-none opacity-0 scale-100"
          : isVisible
            ? "opacity-100 scale-100"
            : "opacity-0 scale-[0.97]"
      )}
    >
      {/* Top bar: call info + timer */}
      <div className="flex items-center justify-between px-3 pt-4 pb-2 sm:px-6 sm:pt-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-500/20">
            {activeCall.type === "video" ? (
              <Video className="h-4.5 w-4.5 text-brand-400" />
            ) : (
              <Phone className="h-4.5 w-4.5 text-brand-400" />
            )}
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white">
              {conversationName}
            </h3>
            <div className="flex items-center gap-2 text-xs text-white/50">
              <span>
                {activeCall.type === "video" ? "Video call" : "Voice call"}
              </span>
              {activeCall.status === "active" && (
                <>
                  <span className="text-white/20">|</span>
                  <CallTimer startedAt={activeCall.startedAt} />
                </>
              )}
            </div>
          </div>
        </div>

        {/* Right side: minimize + status */}
        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          {/* Minimize button */}
          <button
            onClick={onMinimize}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-white/50 transition-colors hover:bg-white/10 hover:text-white"
            aria-label="Minimize call (Esc)"
          >
            <ChevronDown className="h-5 w-5" />
          </button>

          {activeCall.status === "active" && (
            <div className="flex items-center gap-1.5 rounded-full bg-white/5 px-2.5 py-1">
              <Users className="h-3.5 w-3.5 text-white/50" />
              <span className="text-xs font-medium text-white/60">1</span>
            </div>
          )}
          {activeCall.status === "active" && (
            <div className="flex items-center gap-1.5 rounded-full bg-green-500/10 px-3 py-1">
              <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-xs font-medium text-green-400">Live</span>
            </div>
          )}
          <div className="rounded-full bg-yellow-500/10 px-2.5 py-1 text-[10px] font-medium text-yellow-400">
            DEV MOCK
          </div>
        </div>
      </div>

      {/* Error banner with retry */}
      {callError && (
        <div className="mx-3 mt-2 flex items-center gap-3 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 sm:mx-6 sm:px-4 sm:py-2.5">
          <AlertCircle className="h-4 w-4 shrink-0 text-red-400" />
          <span className="flex-1 text-sm text-red-400">{callError}</span>
          <button
            onClick={handleRetry}
            className="flex items-center gap-1 rounded-md bg-red-500/20 px-2.5 py-1 text-xs font-medium text-red-300 transition-colors hover:bg-red-500/30"
          >
            <RotateCcw className="h-3 w-3" />
            Retry
          </button>
          <button
            onClick={() => setCallError(null)}
            className="flex items-center justify-center rounded-md p-1 text-red-400/60 transition-colors hover:text-red-300"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Main content area */}
      {isConnecting ? (
        <ConnectingScreen
          callType={activeCall.type}
          conversationName={conversationName}
          status={activeCall.status}
        />
      ) : (
        <MockActiveCallView
          isCameraOn={localCallState.isCameraOn}
          isScreenSharing={localCallState.isScreenSharing}
        />
      )}

      {/* Controls bar */}
      <CallControls
        isMicOn={localCallState.isMicOn}
        isCameraOn={localCallState.isCameraOn}
        isScreenSharing={localCallState.isScreenSharing}
        isFullscreen={isFullscreen}
        callType={activeCall.type}
        onToggleMic={toggleMic}
        onToggleCamera={toggleCamera}
        onToggleScreenShare={toggleScreenShare}
        onToggleFullscreen={handleToggleFullscreen}
        onEndCall={handleEndCall}
      />

      {/* Keyboard shortcut hints — hidden on mobile */}
      <div className="absolute bottom-2 left-1/2 hidden -translate-x-1/2 text-[10px] text-white/20 sm:block">
        M = mute | V = camera | F = fullscreen | Esc = minimize
      </div>

      {/* CSS animation keyframes */}
      <style>{`
        @keyframes callPulse {
          0%, 100% { opacity: 0.4; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

function HuddleCallOverlayContent({
  activeCall,
  conversationName,
  onMinimize,
  isHidden,
}: {
  activeCall: CallSession;
  conversationName: string;
  onMinimize: () => void;
  isHidden: boolean;
}) {
  const endCall = useMessengerStore((s) => s.endCall);
  const setCallStatus = useMessengerStore((s) => s.setCallStatus);
  const setCallError = useMessengerStore((s) => s.setCallError);
  const callError = useMessengerStore((s) => s.callError);

  const overlayRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [selectedMicId, setSelectedMicId] = useState(
    localStorage.getItem("slumbr:call:micId") ?? ""
  );
  const [selectedCameraId, setSelectedCameraId] = useState(
    localStorage.getItem("slumbr:call:cameraId") ?? ""
  );
  const [selectedSpeakerId, setSelectedSpeakerId] = useState(
    localStorage.getItem("slumbr:call:speakerId") ?? ""
  );
  const selectedMicIdRef = useRef(selectedMicId);
  const selectedCameraIdRef = useRef(selectedCameraId);
  const [peerControls, setPeerControls] = useState<Record<string, PeerAudioControl>>({});
  const [peerMenu, setPeerMenu] = useState<PeerContextMenuState | null>(null);
  const [isPoked, setIsPoked] = useState(false);

  const { joinRoom, leaveRoom } = useRoom({
    onJoin: () => setCallStatus("active"),
    onFailed: (error) => {
      console.error("[Huddle01] join failed", error);
      setCallError("Failed to join call");
    },
  });
  const {
    isAudioOn,
    enableAudio,
    disableAudio,
    changeAudioSource,
  } = useLocalAudio();
  const {
    stream: videoStream,
    isVideoOn,
    enableVideo,
    disableVideo,
    changeVideoSource,
  } = useLocalVideo();
  const { shareStream, startScreenShare, stopScreenShare } =
    useLocalScreenShare();
  const { peerIds } = usePeerIds();
  const { peerId: localPeerId, updateMetadata } = useLocalPeer<CallPeerMetadata>();
  const { sendData } = useDataMessage({
    onMessage: (payload, _from, label) => {
      if (label !== CALL_DATA_LABEL) return;
      try {
        const message = JSON.parse(payload) as { type?: string };
        if (message.type !== "call.poke") return;
        setIsPoked(true);
        window.setTimeout(() => setIsPoked(false), 900);
      } catch {
        return;
      }
    },
  });
  const micDevices = useDevices({ type: "mic" });
  const cameraDevices = useDevices({ type: "cam" });
  const speakerDevices = useDevices({ type: "speaker" });

  useEffect(() => {
    requestAnimationFrame(() => setIsVisible(true));
  }, []);

  useEffect(() => {
    if (!localPeerId) return;
    updateMetadata({
      userId: getCurrentUserId(),
      displayName: getCurrentUserName(),
    }).catch(() => {});
  }, [localPeerId, updateMetadata]);

  useEffect(() => {
    setPeerControls((current) => {
      const next: Record<string, PeerAudioControl> = {};
      for (const peerId of peerIds) {
        next[peerId] = current[peerId] ?? { volume: 1, deafened: false };
      }
      return next;
    });
  }, [peerIds]);

  useEffect(() => {
    selectedMicIdRef.current = selectedMicId;
  }, [selectedMicId]);

  useEffect(() => {
    selectedCameraIdRef.current = selectedCameraId;
  }, [selectedCameraId]);

  useEffect(() => {
    if (!activeCall.roomId || !activeCall.accessToken) return;

    const roomId = activeCall.roomId;
    const accessToken = activeCall.accessToken;

    let cancelled = false;
    async function connect() {
      try {
        await joinRoom({ roomId, token: accessToken });
        if (cancelled) return;
        await enableAudio();
        if (selectedMicIdRef.current) {
          await changeAudioSource(selectedMicIdRef.current);
        }
        if (activeCall.type === "video") {
          await enableVideo();
          if (selectedCameraIdRef.current) {
            await changeVideoSource(selectedCameraIdRef.current);
          }
        }
      } catch (error) {
        console.error("[Huddle01] call setup failed", error);
        if (!cancelled) {
          setCallError("Failed to connect media devices");
        }
      }
    }

    void connect();

    return () => {
      cancelled = true;
      disableAudio().catch(() => {});
      disableVideo().catch(() => {});
      stopScreenShare().catch(() => {});
      leaveRoom();
    };
  }, [
    activeCall.accessToken,
    activeCall.roomId,
    activeCall.type,
    changeAudioSource,
    changeVideoSource,
    disableAudio,
    disableVideo,
    enableAudio,
    enableVideo,
    joinRoom,
    leaveRoom,
    setCallError,
    setCallStatus,
    stopScreenShare,
  ]);

  const handleToggleFullscreen = useCallback(() => {
    if (!overlayRef.current) return;

    if (!document.fullscreenElement) {
      overlayRef.current.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  }, []);

  const handleToggleMic = useCallback(() => {
    if (isAudioOn) disableAudio().catch(() => {});
    else {
      enableAudio()
        .then(() => {
          if (selectedMicIdRef.current) {
            return changeAudioSource(selectedMicIdRef.current);
          }
        })
        .catch(() => {});
    }
  }, [changeAudioSource, disableAudio, enableAudio, isAudioOn]);

  const handleToggleCamera = useCallback(() => {
    if (isVideoOn) disableVideo().catch(() => {});
    else {
      enableVideo()
        .then(() => {
          if (selectedCameraIdRef.current) {
            return changeVideoSource(selectedCameraIdRef.current);
          }
        })
        .catch(() => {});
    }
  }, [changeVideoSource, disableVideo, enableVideo, isVideoOn]);

  const handleToggleScreenShare = useCallback(() => {
    if (shareStream) stopScreenShare().catch(() => {});
    else startScreenShare().catch(() => {});
  }, [shareStream, startScreenShare, stopScreenShare]);

  const handleSelectMic = useCallback(
    (deviceId: string) => {
      setSelectedMicId(deviceId);
      localStorage.setItem("slumbr:call:micId", deviceId);
      micDevices.setPreferredDevice(deviceId);
      if (deviceId && isAudioOn) changeAudioSource(deviceId).catch(() => {});
    },
    [changeAudioSource, isAudioOn, micDevices]
  );

  const handleSelectCamera = useCallback(
    (deviceId: string) => {
      setSelectedCameraId(deviceId);
      localStorage.setItem("slumbr:call:cameraId", deviceId);
      cameraDevices.setPreferredDevice(deviceId);
      if (deviceId && isVideoOn) changeVideoSource(deviceId).catch(() => {});
    },
    [cameraDevices, changeVideoSource, isVideoOn]
  );

  const handleSelectSpeaker = useCallback(
    (deviceId: string) => {
      setSelectedSpeakerId(deviceId);
      localStorage.setItem("slumbr:call:speakerId", deviceId);
      speakerDevices.setPreferredDevice(deviceId);
    },
    [speakerDevices]
  );

  const handleRefreshDevices = useCallback(() => {
    micDevices.refreshDevices();
    cameraDevices.refreshDevices();
    speakerDevices.refreshDevices();
  }, [cameraDevices, micDevices, speakerDevices]);

  const handlePeerContextMenu = useCallback(
    (
      event: MouseEvent<HTMLDivElement>,
      peerId: string,
      displayName: string
    ) => {
      event.preventDefault();
      const menuWidth = 256;
      const menuHeight = 270;
      setPeerMenu({
        peerId,
        displayName,
        x: Math.min(event.clientX, window.innerWidth - menuWidth - 12),
        y: Math.min(event.clientY, window.innerHeight - menuHeight - 12),
      });
    },
    []
  );

  const handlePeerVolumeChange = useCallback((peerId: string, volume: number) => {
    setPeerControls((current) => ({
      ...current,
      [peerId]: {
        ...(current[peerId] ?? { volume: 1, deafened: false }),
        volume,
      },
    }));
  }, []);

  const handleTogglePeerDeafen = useCallback((peerId: string) => {
    setPeerControls((current) => {
      const existing = current[peerId] ?? { volume: 1, deafened: false };
      return {
        ...current,
        [peerId]: { ...existing, deafened: !existing.deafened },
      };
    });
  }, []);

  const handlePokePeer = useCallback(
    (peerId: string) => {
      void sendData({
        to: [peerId],
        label: CALL_DATA_LABEL,
        payload: JSON.stringify({
          type: "call.poke",
          fromUserId: getCurrentUserId(),
          fromName: getCurrentUserName(),
        }),
      });
      setPeerMenu(null);
    },
    [sendData]
  );

  const handleEndCall = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    endCall();
  }, [endCall]);

  const isConnecting =
    activeCall.status === "creating" ||
    activeCall.status === "connecting" ||
    activeCall.status === "ringing" ||
    activeCall.status === "reconnecting";

  return (
    <div
      ref={overlayRef}
      className={cn(
        "absolute inset-0 z-50 flex flex-col bg-gray-950 transition-all duration-300",
        isPoked && "call-poked",
        isHidden
          ? "pointer-events-none opacity-0 scale-100"
          : isVisible
            ? "opacity-100 scale-100"
            : "opacity-0 scale-[0.97]"
      )}
    >
      <div className="flex items-center justify-between px-3 pb-2 pt-4 sm:px-6 sm:pt-6">
        <div>
          <h3 className="text-sm font-semibold text-white">{conversationName}</h3>
          <div className="flex items-center gap-2 text-xs text-white/50">
            <span>{activeCall.type === "video" ? "Video call" : "Voice call"}</span>
            {activeCall.status === "active" && (
              <>
                <span className="text-white/20">|</span>
                <CallTimer startedAt={activeCall.startedAt} />
              </>
            )}
          </div>
        </div>
        <button
          onClick={onMinimize}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-white/50 transition-colors hover:bg-white/10 hover:text-white"
          aria-label="Minimize call"
        >
          <ChevronDown className="h-5 w-5" />
        </button>
      </div>

      {callError && (
        <div className="mx-3 mt-2 flex items-center gap-3 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 sm:mx-6">
          <AlertCircle className="h-4 w-4 shrink-0 text-red-400" />
          <span className="flex-1 text-sm text-red-400">{callError}</span>
        </div>
      )}

      {isConnecting ? (
        <ConnectingScreen
          callType={activeCall.type}
          conversationName={conversationName}
          status={activeCall.status}
        />
      ) : (
        <div className="grid flex-1 grid-cols-1 gap-2 p-3 sm:grid-cols-2 sm:p-4">
          <div className="relative min-h-0 overflow-hidden rounded-2xl bg-surface-dark-2">
            {isVideoOn && videoStream ? (
              <StreamVideo
                stream={videoStream}
                muted
                className="h-full w-full object-cover"
              />
            ) : (
              <LocalAvatarPreview displayName={getCurrentUserName()} />
            )}
            <div className="absolute left-2 top-2 rounded-md bg-black/40 px-2 py-1 text-[10px] text-white/80">
              {getCurrentUserName()}
            </div>
          </div>
          {shareStream ? (
            <div className="relative min-h-0 overflow-hidden rounded-2xl bg-surface-dark-2 sm:col-span-2">
              <StreamVideo
                stream={shareStream}
                muted
                className="h-full w-full object-contain"
              />
            </div>
          ) : peerIds.length > 0 ? (
            peerIds.map((peerId) => (
              <RemotePeerTile
                key={peerId}
                peerId={peerId}
                audioControl={peerControls[peerId] ?? { volume: 1, deafened: false }}
                speakerDeviceId={selectedSpeakerId || undefined}
                onContextMenu={handlePeerContextMenu}
              />
            ))
          ) : (
            <div className="flex items-center justify-center rounded-2xl border-2 border-dashed border-white/10 bg-surface-dark-2/30">
              <div className="text-center">
                <Users className="mx-auto h-8 w-8 text-white/20" />
                <p className="mt-2 text-sm text-white/30">Waiting for others to join...</p>
              </div>
            </div>
          )}
        </div>
      )}

      {isSettingsOpen && (
        <CallSettingsPanel
          micDevices={micDevices.devices}
          cameraDevices={cameraDevices.devices}
          speakerDevices={speakerDevices.devices}
          selectedMicId={selectedMicId || micDevices.preferredDeviceId || ""}
          selectedCameraId={selectedCameraId || cameraDevices.preferredDeviceId || ""}
          selectedSpeakerId={selectedSpeakerId || speakerDevices.preferredDeviceId || ""}
          onSelectMic={handleSelectMic}
          onSelectCamera={handleSelectCamera}
          onSelectSpeaker={handleSelectSpeaker}
          onRefreshDevices={handleRefreshDevices}
        />
      )}

      {peerMenu && (
        <PeerContextMenu
          menu={peerMenu}
          control={peerControls[peerMenu.peerId] ?? { volume: 1, deafened: false }}
          onClose={() => setPeerMenu(null)}
          onVolumeChange={handlePeerVolumeChange}
          onToggleDeafen={handleTogglePeerDeafen}
          onPoke={handlePokePeer}
        />
      )}

      <CallControls
        isMicOn={isAudioOn}
        isCameraOn={isVideoOn}
        isScreenSharing={!!shareStream}
        isFullscreen={isFullscreen}
        isSettingsOpen={isSettingsOpen}
        callType={activeCall.type}
        onToggleMic={handleToggleMic}
        onToggleCamera={handleToggleCamera}
        onToggleScreenShare={handleToggleScreenShare}
        onToggleFullscreen={handleToggleFullscreen}
        onToggleSettings={() => setIsSettingsOpen((open) => !open)}
        onEndCall={handleEndCall}
      />

      {isPoked && (
        <div className="pointer-events-none absolute inset-x-0 top-20 z-20 flex justify-center">
          <div className="rounded-full bg-yellow-300 px-4 py-2 text-sm font-semibold text-gray-950 shadow-xl shadow-yellow-300/30">
            You got poked
          </div>
        </div>
      )}

      <style>{`
        @keyframes callPokeFlash {
          0%, 100% { filter: brightness(1); }
          18%, 55% { filter: brightness(1.55) saturate(1.25); }
        }
        @keyframes callPokeShake {
          0%, 100% { transform: translateX(0); }
          15% { transform: translateX(-8px); }
          30% { transform: translateX(8px); }
          45% { transform: translateX(-5px); }
          60% { transform: translateX(5px); }
          75% { transform: translateX(-2px); }
        }
        .call-poked {
          animation: callPokeFlash 850ms ease, callPokeShake 520ms ease;
        }
      `}</style>
    </div>
  );
}

// ─── Main Call Overlay (thin wrapper — no hooks) ─────────────────────────────
// Reads activeCall from store and renders inside the messenger route window.
// Picks MockCallOverlayContent (dev) vs future HuddleCallOverlayContent (prod).
// Hides (but stays mounted) when minimized — PersistentBar mini widget takes over.

export function CallOverlay() {
  const activeCall = useMessengerStore((s) => s.activeCall);
  const localCallState = useMessengerStore((s) => s.localCallState);
  const conversations = useMessengerStore((s) => s.conversations);
  const endCall = useMessengerStore((s) => s.endCall);
  const isCallMinimized = useMessengerStore((s) => s.isCallMinimized);
  const minimizeCall = useMessengerStore((s) => s.minimizeCall);

  const conversationName = useMemo(() => {
    if (!activeCall) return "Call";
    const conversation = conversations.find((c) => c.id === activeCall.conversationId);
    return conversation ? getDisplayName(conversation) : "Call";
  }, [activeCall, conversations]);

  if (!activeCall) return null;

  const content = isMockRoom(activeCall.roomId) ? (
    <MockCallOverlayContent
      activeCall={activeCall}
      localCallState={localCallState}
      conversationName={conversationName}
      onMinimize={minimizeCall}
      isHidden={isCallMinimized}
    />
  ) : (
    <HuddleCallOverlayContent
      activeCall={activeCall}
      conversationName={conversationName}
      onMinimize={minimizeCall}
      isHidden={isCallMinimized}
    />
  );

  return (
    <CallOverlayErrorBoundary onEndCall={endCall}>
      {content}
    </CallOverlayErrorBoundary>
  );
}
